'use client';

import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { IPrismicImageType } from '@/types';

import { Close } from '../../atoms/close';
import { PrismicImageWithAlt } from '@/components/PrismicImageWithAlt';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getVisibleFocusable = (root: HTMLElement) =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    el =>
      !el.closest('[hidden], [aria-hidden="true"]') &&
      el.getClientRects().length > 0,
  );

interface IModalProps {
  isAgeGate?: boolean;
  children: ReactNode;
  isOpen: boolean;
  sm?: boolean;
  bgImage?: IPrismicImageType;
  popupBgColor?: string;
  overlayBgColor?: string;
  overlayOpacity?: number;
  md?: boolean;
  lg?: boolean;
  className?: string;
  toggle?: () => void;
  /** When false, children own the Close control (Escape/overlay still use toggle). */
  showCloseButton?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
}

const Modal: FC<IModalProps> = ({
  children,
  isOpen = false,
  toggle,
  showCloseButton = true,
  sm,
  md,
  lg,
  className,
  bgImage,
  popupBgColor,
  overlayBgColor,
  overlayOpacity,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  ...props
}) => {
  const [localDocument, setLocalDocument] = useState<Document | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;

  const overlay = {
    background: overlayBgColor ?? 'black',
    opacity: overlayOpacity ?? 0.2,
  };

  useEffect(() => {
    setLocalDocument(document);
    const { clientWidth, style } = document.body;
    const overflow = window.innerWidth - clientWidth;

    if (isOpen && !overflow) {
      return;
    }

    style.overflowY = isOpen ? 'hidden' : 'unset';
    style.paddingRight = isOpen ? `${Math.abs(overflow)}px` : '0';
  }, [isOpen]);

  // Initial focus + Tab trap + Escape dismiss; restore focus on close/unmount.
  useEffect(() => {
    if (!isOpen || !localDocument) return;

    const previousFocus = document.activeElement as HTMLElement | null;

    const getContentRoot = () =>
      dialogRef.current?.querySelector<HTMLElement>('.modal__content') ??
      dialogRef.current;

    const timer = window.setTimeout(() => {
      const content = getContentRoot();
      if (!content) return;

      const focusable = getVisibleFocusable(content);
      const closeBtn = focusable.find(
        el => el.getAttribute('aria-label')?.toLowerCase() === 'close',
      );
      (closeBtn ?? focusable[0] ?? dialogRef.current)?.focus();
    }, 50);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!toggleRef.current) return;
        event.preventDefault();
        toggleRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const content = getContentRoot();
      if (!content) return;

      const focusable = getVisibleFocusable(content);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (active === first || !content.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !content.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown);
      if (
        previousFocus &&
        typeof previousFocus.focus === 'function' &&
        document.contains(previousFocus)
      ) {
        previousFocus.focus();
      }
    };
  }, [isOpen, localDocument]);

  if (!isOpen) {
    return null;
  }

  if (!localDocument) return null;

  const renderBuiltInClose = Boolean(toggle) && showCloseButton;

  return createPortal(
    <div
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-modal="true"
      className={classNames(
        'modal',
        { modal_md: md, modal_sm: sm, modal_lg: lg },
        className,
      )}
      ref={dialogRef}
      role="dialog"
      tabIndex={-1}
      {...props}
    >
      {bgImage && (
        <PrismicImageWithAlt
          className="image__overlay"
          defaultAlt="Background image"
          field={bgImage}
          fill
          style={{ objectFit: 'cover', objectPosition: 'center center' }}
        />
      )}
      <div
        aria-hidden="true"
        className="modal__overlay"
        onClick={toggle}
        style={overlay}
      />

      <div
        className={classNames('modal__content', {
          modal__content_closable: renderBuiltInClose,
        })}
        role="document"
        style={{
          background: popupBgColor,
        }}
      >
        {renderBuiltInClose && (
          <Close className={'close'} data-dismiss="dialog" onClick={toggle} />
        )}
        <div className="modal__body">{children}</div>
      </div>
    </div>,
    localDocument?.body,
  );
};

export default Modal;
